import type {
    DropdownData,
    FilteredValue,
    ReactiveFormSupportDataDiagnosticsThreshold,
    ReactiveFormSupportDataSource,
} from '@fitogest/reactive-form-builder/interfaces';
import { ReactiveFormSupportDataSourceFactory } from '@fitogest/reactive-form-builder/utils';
import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type { MantenimientoModel } from 'src/app/models/trabajadores/mantenimiento.model';
import type { RequestHandler } from 'src/app/service/OffService/request-handler';
import type { ParcelasEnvironmentDefinition } from '../environment-variables';
import type { ParcelasFormDefinition } from './form-definition';
import {
    PARCELAS_FORM_SUPPORT_DATA_KEYS,
    ParcelasFormLoadPlan,
    type ParcelasFormSupportDataKey,
} from './parcelas-form-load-plan';

const PARCELAS_SUPPORT_DATA_TTL_MS = 300000;
const PARCELAS_SUPPORT_DATA_STALE_TTL_MS = 86400000;
const PARCELAS_SUPPORT_DATA_MAX_CACHE_MS = 300;
const PARCELAS_SUPPORT_DATA_MAX_AVERAGE_MS = 500;
const PARCELAS_SUPPORT_DATA_MAX_MS = 1500;
const PARCELAS_SUPPORT_DATA_MAX_NETWORK_MS = 1200;

const PARCELAS_DEFAULT_SUPPORT_DATA_DIAGNOSTICS_THRESHOLD: ReactiveFormSupportDataDiagnosticsThreshold = {
    maxAverageMs: PARCELAS_SUPPORT_DATA_MAX_AVERAGE_MS,
    maxMs: PARCELAS_SUPPORT_DATA_MAX_MS,
    maxAverageNetworkMs: PARCELAS_SUPPORT_DATA_MAX_NETWORK_MS,
    maxCacheMs: PARCELAS_SUPPORT_DATA_MAX_CACHE_MS,
};

const PARCELAS_SUPPORT_DATA_DIAGNOSTICS_THRESHOLDS: Partial<Record<ParcelasFormSupportDataKey, ReactiveFormSupportDataDiagnosticsThreshold>> = {
    [PARCELAS_FORM_SUPPORT_DATA_KEYS.fincas]: {
        maxCacheMs: PARCELAS_SUPPORT_DATA_MAX_CACHE_MS,
        maxAverageNetworkMs: PARCELAS_SUPPORT_DATA_MAX_NETWORK_MS,
    },
    [PARCELAS_FORM_SUPPORT_DATA_KEYS.sectores]: {
        maxCacheMs: PARCELAS_SUPPORT_DATA_MAX_CACHE_MS,
        maxAverageNetworkMs: PARCELAS_SUPPORT_DATA_MAX_NETWORK_MS,
    },
};

function createParcelasDiagnosticsThreshold(key: ParcelasFormSupportDataKey): ReactiveFormSupportDataDiagnosticsThreshold {
    return {
        ...PARCELAS_DEFAULT_SUPPORT_DATA_DIAGNOSTICS_THRESHOLD,
        ...(PARCELAS_SUPPORT_DATA_DIAGNOSTICS_THRESHOLDS[key] ?? {}),
    };
}

export interface ParcelasSupportDataSourceEndpoints {
    readonly portainjertosGET: RequestHandler<any>;
    readonly sistemasRiegoGET: RequestHandler<any>;
    readonly comunidadesRegantesGET: RequestHandler<any>;
    readonly origenesAguaRiegoGET: RequestHandler<any>;
    readonly fincasGET: RequestHandler<any>;
    readonly sectoresGET: RequestHandler<any>;
    readonly compradoresGET: RequestHandler<any>;
    readonly usuariosGET: RequestHandler<any>;
    readonly trabajadoresGET: RequestHandler<any>;
    readonly cultivosGET: RequestHandler<any>;
    readonly variedadesGET: RequestHandler<any>;
}

export interface ParcelasSupportDataSourcesFactoryContext {
    readonly form: ParcelasFormDefinition;
    readonly formType: number;
    readonly env: ParcelasEnvironmentDefinition;
    readonly endpoints: ParcelasSupportDataSourceEndpoints;
    readonly addDefaultSequiadorAplicador: (row: MantenimientoModel) => void;
}

/** Creates executable support-data declarations for the Parcelas load session. */
export class ParcelasSupportDataSourcesFactory {
    private static readonly sourceFactory = new ReactiveFormSupportDataSourceFactory<ParcelasModel, ParcelasFormSupportDataKey>({
        ttlMs: PARCELAS_SUPPORT_DATA_TTL_MS,
        staleTtlMs: PARCELAS_SUPPORT_DATA_STALE_TTL_MS,
        cacheStorage: 'persistent',
        staleWhileRevalidate: true,
        diagnosticsThreshold: createParcelasDiagnosticsThreshold,
    });

    public static create(
        context: ParcelasSupportDataSourcesFactoryContext,
    ): readonly ReactiveFormSupportDataSource<ParcelasModel>[] {
        return ParcelasFormLoadPlan.createSupportDataSources({
            formType: context.formType,
            env: context.env,
        }).map((source) => ParcelasSupportDataSourcesFactory.createSourceForKey(
            context,
            source.key as ParcelasFormSupportDataKey,
            source.phase,
        ));
    }

    public static usesDeclarativeFincaSectorDropdowns(formType: number): boolean {
        return ParcelasFormLoadPlan.usesDeclarativeFincaSectorDropdowns(formType);
    }

    public static usesDeclarativeSimpleDropdowns(formType: number): boolean {
        return ParcelasFormLoadPlan.usesDeclarativeSimpleDropdowns(formType);
    }

    public static shouldLoadLegacyCultivoCatalogs(env: ParcelasEnvironmentDefinition): boolean {
        return ParcelasFormLoadPlan.shouldLoadLegacyCultivoCatalogs(env);
    }

    private static createSourceForKey(
        context: ParcelasSupportDataSourcesFactoryContext,
        key: ParcelasFormSupportDataKey,
        phase: ReactiveFormSupportDataSource<ParcelasModel>['phase'],
    ): ReactiveFormSupportDataSource<ParcelasModel> {
        switch (key) {
        case PARCELAS_FORM_SUPPORT_DATA_KEYS.patronVina:
            return this.createDropdownSource(
                context,
                key,
                phase,
                context.endpoints.portainjertosGET,
                context.form.lists.patronVina,
                ['sinonimo'],
            );
        case PARCELAS_FORM_SUPPORT_DATA_KEYS.sistemasRiego:
            return this.createDropdownSource(
                context,
                key,
                phase,
                context.endpoints.sistemasRiegoGET,
                context.form.lists.sistemasRiego,
                ['sistema_de_riego'],
            );
        case PARCELAS_FORM_SUPPORT_DATA_KEYS.comunidadesRegantes:
            return this.createDropdownSource(
                context,
                key,
                phase,
                context.endpoints.comunidadesRegantesGET,
                context.form.lists.comunidadesRegantes,
                'nombre',
            );
        case PARCELAS_FORM_SUPPORT_DATA_KEYS.origenesAguaRiego:
            return this.createDropdownSource(
                context,
                key,
                phase,
                context.endpoints.origenesAguaRiegoGET,
                context.form.lists.origenesAguaRiego,
                'procedencia_del_agua_de_riego',
            );
        case PARCELAS_FORM_SUPPORT_DATA_KEYS.fincas:
            return this.createDropdownSource(context, key, phase, context.endpoints.fincasGET, context.form.lists.fincas, ['nombre']);
        case PARCELAS_FORM_SUPPORT_DATA_KEYS.sectores:
            return this.createDropdownSource(context, key, phase, context.endpoints.sectoresGET, context.form.lists.sectores, ['nombre']);
        case PARCELAS_FORM_SUPPORT_DATA_KEYS.compradores:
            return this.createDropdownSource(
                context,
                key,
                phase,
                context.endpoints.compradoresGET,
                context.form.lists.comercios,
                'razon_social',
            );
        case PARCELAS_FORM_SUPPORT_DATA_KEYS.aplicadores:
            return this.createAplicadoresSource(context, key, phase);
        case PARCELAS_FORM_SUPPORT_DATA_KEYS.usuarios:
            return this.createDropdownSource(
                context,
                key,
                phase,
                context.endpoints.usuariosGET,
                context.form.lists.usuarios,
                (row) => this.createNombreApellidosLabel(row),
            );
        case PARCELAS_FORM_SUPPORT_DATA_KEYS.legacyCultivos:
            return this.createLegacyCultivosSource(context, key, phase);
        case PARCELAS_FORM_SUPPORT_DATA_KEYS.legacyVariedades:
            return this.createDropdownSource(
                context,
                key,
                phase,
                context.endpoints.variedadesGET,
                context.form.lists.variedadesLegacy,
                ['variedad'],
            );
        }
    }

    private static createDropdownSource(
        context: ParcelasSupportDataSourcesFactoryContext,
        key: ParcelasFormSupportDataKey,
        phase: ReactiveFormSupportDataSource<ParcelasModel>['phase'],
        endpoint: RequestHandler<any>,
        target: DropdownData<unknown>,
        label: ReactiveFormSupportDataSource<ParcelasModel>['label'],
    ): ReactiveFormSupportDataSource<ParcelasModel> {
        return this.sourceFactory.dropdownSource({
            key,
            phase,
            endpoint,
            target,
            label,
            cacheKey: this.createCacheKey(context, key),
        });
    }

    private static createAplicadoresSource(
        context: ParcelasSupportDataSourcesFactoryContext,
        key: ParcelasFormSupportDataKey,
        phase: ReactiveFormSupportDataSource<ParcelasModel>['phase'],
    ): ReactiveFormSupportDataSource<ParcelasModel> {
        const endpoint = context.env.appName === 'sequiador'
            ? context.endpoints.usuariosGET
            : context.endpoints.trabajadoresGET;

        return this.createDropdownSource(
            context,
            key,
            phase,
            endpoint,
            context.form.lists.aplicadores,
            (row) => {
                if (context.env.appName === 'sequiador') {
                    context.addDefaultSequiadorAplicador(row as MantenimientoModel);
                }

                return this.createNombreApellidosLabel(row);
            },
        );
    }

    private static createLegacyCultivosSource(
        context: ParcelasSupportDataSourcesFactoryContext,
        key: ParcelasFormSupportDataKey,
        phase: ReactiveFormSupportDataSource<ParcelasModel>['phase'],
    ): ReactiveFormSupportDataSource<ParcelasModel> {
        return this.sourceFactory.source({
            key,
            phase,
            endpoint: context.endpoints.cultivosGET,
            target: context.form.lists.cultivosLegacy,
            cacheKey: this.createCacheKey(context, key),
            preserveExistingValues: false,
            mapResponse: (response) => this.buildDistinctLegacyCultivoOptions(Array.isArray(response) ? response : []),
        });
    }

    private static buildDistinctLegacyCultivoOptions(rows: readonly unknown[]): FilteredValue<unknown>[] {
        const seen = new Set<string>();
        const options: FilteredValue<unknown>[] = [];

        for (const row of rows) {
            const record = this.toRecord(row);
            const label = String(record['cultivo'] ?? '').trim();
            const distinctKey = this.normalizeDropdownDistinctToken(label);

            if (label === '' || distinctKey === '' || seen.has(distinctKey)) {
                continue;
            }

            seen.add(distinctKey);
            options.push({
                label,
                value: row,
                raw: row,
                valueAliases: this.buildLegacyCultivoAliases(record, label),
            });
        }

        return [
            { label: '...', value: null },
            ...options.sort((first, second) => first.label.localeCompare(second.label, undefined, { sensitivity: 'base' })),
        ];
    }

    private static buildLegacyCultivoAliases(record: Record<string, unknown>, label: string): string[] {
        return Array.from(new Set([
            record['id'],
            record['codigo'],
            record['codigo_cultivo'],
            record['id_cultivo'],
            label,
        ]
            .map((value) => String(value ?? '').trim())
            .filter((value) => value !== '')));
    }

    private static normalizeDropdownDistinctToken(value: unknown): string {
        return String(value ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();
    }

    private static createNombreApellidosLabel(row: unknown): string {
        const record = this.toRecord(row);

        return String(record['nombre'] ?? '') + ' ' + String(record['apellidos'] ?? '');
    }

    private static createCacheKey(context: ParcelasSupportDataSourcesFactoryContext, key: ParcelasFormSupportDataKey): string {
        return `parcelas.${context.env.appName}.${context.formType}.${key}`;
    }

    private static toRecord(value: unknown): Record<string, unknown> {
        return value !== null && typeof value === 'object' ? value as Record<string, unknown> : {};
    }
}
